import React from 'react';
import {
  getResponsiveImageUrls,
  getOptimizedImageUrl,
  getThumbnailUrl,
  getAvatarUrl,
  getBannerUrl,
  getProductImageUrl,
  getResponsiveProductImageUrls,
  getResponsiveBannerUrls,
  getAutoWidthImageUrl,
  getSlowConnectionImageUrl,
  ImageTransformOptions
} from '../../lib/cloudflare-images';

interface OptimizedImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'sizes'> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  quality?: number;
  format?: 'auto' | 'webp' | 'avif' | 'jpeg' | 'png' | 'gif';
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  gravity?: 'auto' | 'left' | 'right' | 'top' | 'bottom' | 'center';
  blur?: number;
  sharpen?: number;
  saturation?: number;
  brightness?: number;
  contrast?: number;
  gamma?: number;
  rotate?: number;
  flip?: 'horizontal' | 'vertical';
  trim?: number;
  onerror?: 'redirect';
  responsive?: boolean;
  responsiveSizes?: number[];
  sizes?: string;
  lazy?: boolean;
  placeholder?: string;
  domain?: string;
  type?: 'product' | 'banner' | 'avatar' | 'thumbnail' | 'auto';
}

/**
 * Componente de imagen optimizada que usa las transformaciones de Cloudflare
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  quality,
  format = 'auto',
  fit,
  gravity,
  blur,
  sharpen,
  saturation,
  brightness,
  contrast,
  gamma,
  rotate,
  flip,
  trim,
  onerror,
  responsive = false,
  responsiveSizes = [320, 480, 640, 768, 1024, 1280],
  sizes,
  lazy = true,
  placeholder,
  domain,
  type = 'auto',
  className,
  style,
  ...props
}) => {
  // Generar URL optimizada basada en el tipo
  const getOptimizedSrc = () => {
    const baseOptions: ImageTransformOptions = {
      width,
      height,
      quality,
      format,
      fit,
      gravity,
      blur,
      sharpen,
      saturation,
      brightness,
      contrast,
      gamma,
      rotate,
      flip,
      trim,
      onerror
    };

    switch (type) {
      case 'product':
        return getProductImageUrl(src, width || 640, domain);
      case 'banner':
        return getBannerUrl(src, width || 1920, height || 600, domain);
      case 'avatar':
        return getAvatarUrl(src, width || 100, domain);
      case 'thumbnail':
        return getThumbnailUrl(src, width || 300, height || 200, domain);
      case 'auto':
      default:
        return getOptimizedImageUrl(src, baseOptions, domain);
    }
  };

  // Generar URLs responsivas si está habilitado
  const getResponsiveUrls = () => {
    if (!responsive) return null;

    const baseOptions: Omit<ImageTransformOptions, 'width'> = {
      height,
      quality,
      format,
      fit,
      gravity,
      blur,
      sharpen,
      saturation,
      brightness,
      contrast,
      gamma,
      rotate,
      flip,
      trim,
      onerror
    };

    switch (type) {
      case 'product':
        return getResponsiveProductImageUrls(src, domain);
      case 'banner':
        return getResponsiveBannerUrls(src, domain);
      default:
        return getResponsiveImageUrls(src, responsiveSizes, baseOptions, domain);
    }
  };

  const optimizedSrc = getOptimizedSrc();
  const responsiveUrls = getResponsiveUrls();

  return (
    <img
      src={optimizedSrc}
      alt={alt}
      width={width}
      height={height}
      loading={lazy ? 'lazy' : 'eager'}
      className={className}
      style={style}
      srcSet={responsiveUrls?.srcset}
      sizes={responsiveUrls ? (sizes || '(max-width: 768px) 100vw, 50vw') : sizes}
      {...props}
    />
  );
};

/**
 * Componente de imagen responsiva con múltiples tamaños
 */
export const ResponsiveImage: React.FC<Omit<OptimizedImageProps, 'responsive'> & {
  responsiveSizes?: number[];
  sizesAttr?: string;
}> = ({ responsiveSizes = [320, 480, 640, 768, 1024, 1280], sizesAttr, ...props }) => {
  return (
    <OptimizedImage
      {...props}
      responsive={true}
      responsiveSizes={responsiveSizes}
      sizes={sizesAttr}
    />
  );
};

/**
 * Componente de imagen de producto optimizada
 */
export const ProductImage: React.FC<Omit<OptimizedImageProps, 'type'> & {
  size?: number;
}> = ({ size = 640, ...props }) => {
  return (
    <OptimizedImage
      {...props}
      type="product"
      width={size}
      height={size}
    />
  );
};

/**
 * Componente de imagen de banner optimizada
 */
export const BannerImage: React.FC<Omit<OptimizedImageProps, 'type'> & {
  width?: number;
  height?: number;
}> = ({ width = 1920, height = 600, ...props }) => {
  return (
    <OptimizedImage
      {...props}
      type="banner"
      width={width}
      height={height}
    />
  );
};

/**
 * Componente de avatar optimizado
 */
export const AvatarImage: React.FC<Omit<OptimizedImageProps, 'type'> & {
  size?: number;
}> = ({ size = 100, ...props }) => {
  return (
    <OptimizedImage
      {...props}
      type="avatar"
      width={size}
      height={size}
    />
  );
};

/**
 * Componente de thumbnail optimizado
 */
export const ThumbnailImage: React.FC<Omit<OptimizedImageProps, 'type'> & {
  width?: number;
  height?: number;
}> = ({ width = 300, height = 200, ...props }) => {
  return (
    <OptimizedImage
      {...props}
      type="thumbnail"
      width={width}
      height={height}
    />
  );
};

/**
 * Componente de imagen con ancho automático
 */
export const AutoWidthImage: React.FC<Omit<OptimizedImageProps, 'width' | 'type'> & {
  options?: Omit<ImageTransformOptions, 'width'>;
}> = ({ options = {}, ...props }) => {
  const optimizedSrc = getAutoWidthImageUrl(props.src, options, props.domain);
  
  return (
    <img
      {...props}
      src={optimizedSrc}
      loading="lazy"
    />
  );
};

/**
 * Componente de imagen optimizada para conexiones lentas
 */
export const SlowConnectionImage: React.FC<OptimizedImageProps> = (props) => {
  const optimizedSrc = getSlowConnectionImageUrl(props.src, {
    width: props.width,
    height: props.height,
    quality: props.quality,
    format: props.format,
    fit: props.fit,
    gravity: props.gravity,
    blur: props.blur,
    sharpen: props.sharpen,
    saturation: props.saturation,
    brightness: props.brightness,
    contrast: props.contrast,
    gamma: props.gamma,
    rotate: props.rotate,
    flip: props.flip,
    trim: props.trim,
    onerror: props.onerror
  }, props.domain);
  
  return (
    <img
      {...props}
      src={optimizedSrc}
      loading="lazy"
    />
  );
}; 